import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { categorize, coverageLabel, type AlertCategory, type TenantAlert } from '@ww/shared';
import { api } from '../api';
import { PageHeader } from '../components/ui';
import { useLive } from '../hooks/useLive';
import { I, Icon, KIND_ICON } from '../lib/icons';
import {
  NOTIFICATION_COLORS,
  NOTIFICATION_LABELS,
  affectedCounts,
  errorMessage,
  kindStyle,
} from '../lib/status';
import { fmtDateTime, fmtTime } from '../lib/time';

type Filter = 'all' | 'warning' | 'watch' | 'advisory';
const FILTERS: [Filter, string][] = [
  ['all', 'All'],
  ['warning', 'Warnings'],
  ['watch', 'Watches'],
  ['advisory', 'Advisories'],
];
const EMPTY: TenantAlert[] = [];

function catOf(ta: TenantAlert): AlertCategory {
  return ta.evaluation?.category ?? ta.alert?.category ?? categorize(ta.alert?.event ?? '');
}
function bucket(ta: TenantAlert): Exclude<Filter, 'all'> {
  const c = catOf(ta);
  return c === 'statement' ? 'advisory' : c;
}

export default function Alerts() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { dashboard, tenant, health } = useLive();
  const raw = params.get('filter');
  const filter: Filter = raw === 'warning' || raw === 'watch' || raw === 'advisory' ? raw : 'all';
  const tz = tenant?.timezone;
  const alerts = dashboard?.alerts ?? EMPTY;
  const county = coverageLabel(tenant);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: alerts.length, warning: 0, watch: 0, advisory: 0 };
    for (const ta of alerts) c[bucket(ta)] += 1;
    return c;
  }, [alerts]);
  const rows = filter === 'all' ? alerts : alerts.filter((ta) => bucket(ta) === filter);

  const [history, setHistory] = useState<TenantAlert[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const activeKey = alerts.map((a) => a.alert.nwsId).join('|');
  useEffect(() => {
    let cancelled = false;
    api
      .alertHistory(50)
      .then((list) => {
        if (cancelled) return;
        setHistory(Array.isArray(list) ? list : []);
        setHistoryError(null);
      })
      .catch((e) => {
        if (!cancelled) setHistoryError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [activeKey, dashboard?.tenant?._id]);
  const expired = (history ?? []).filter((ta) => ta?.alert && (!ta.alert.active || ta.evaluation?.state !== 'active'));

  const stale = !!health?.stale;

  return (
    <div className="page page-scroll">
      <PageHeader title="Alerts" sub={`Official NWS products evaluated against ${county} and its monitored assets`}>
        <div role="group" aria-label="Filter alerts" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className="chip hov"
              aria-pressed={filter === key}
              style={{ height: 38, padding: '0 14px', borderRadius: 19, fontSize: 14 }}
              onClick={() => setParams(key === 'all' ? {} : { filter: key }, { replace: true })}
            >
              {label} {dashboard ? counts[key] : ''}
            </button>
          ))}
        </div>
      </PageHeader>

      <div className="panel" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
        <div className="lbl alert-head" style={{ borderBottom: '1px solid #1e2c46' }}>
          <span>Event</span>
          <span>Issued</span>
          <span>Expires</span>
          <span>Severity</span>
          <span>Assets</span>
          <span>Notification</span>
          <span />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {!dashboard && <div className="empty">Loading alerts…</div>}
          {dashboard && rows.length === 0 && (
            <div className="empty" style={{ padding: '60px 20px', fontSize: 16 }}>
              No alerts in this category for {county} right now.
            </div>
          )}
          {rows.map((ta) => {
            const a = ta.alert;
            const k = kindStyle(a.event);
            const ns = ta.evaluation?.notificationState ?? 'none';
            return (
              <button
                key={a.nwsId}
                type="button"
                className="hov alert-row"
                onClick={() => navigate(`/alerts/${encodeURIComponent(a.nwsId)}`)}
                style={{
                  border: 'none',
                  borderBottom: '1px solid #18253c',
                  background: 'transparent',
                  textAlign: 'left',
                  fontSize: 14,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <span
                    style={{
                      width: 42,
                      height: 42,
                      flexShrink: 0,
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: k.tint,
                    }}
                  >
                    <Icon d={KIND_ICON[k.kind]} size={22} style={{ color: k.color }} />
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16, fontWeight: 600, color: k.color }}>{a.event}</span>
                      {stale && <span className="tag tag-stale">STALE</span>}
                    </span>
                    <span
                      style={{ color: '#c2cedf', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={a.areaDesc}
                    >
                      {a.areaDesc || '—'}
                    </span>
                  </span>
                </span>
                <span className="mono" style={{ color: '#d3ddeb' }}>
                  {fmtTime(a.sent, tz)}
                </span>
                <span className="mono" style={{ color: '#d3ddeb' }}>
                  {fmtTime(a.expires ?? a.ends, tz)}
                </span>
                <span style={{ color: '#d3ddeb' }}>
                  {a.severity || '—'} · {a.urgency || '—'}
                </span>
                <span style={{ color: '#d3ddeb' }}>{affectedCounts(ta.evaluation)}</span>
                <span style={{ color: NOTIFICATION_COLORS[ns] ?? '#9fb0c8' }}>
                  {NOTIFICATION_LABELS[ns] ?? ns}
                  {ta.evaluation?.lastNotifiedAt ? ` · ${fmtTime(ta.evaluation.lastNotifiedAt, tz)}` : ''}
                </span>
                <Icon d={I.chevR} size={18} style={{ color: '#9fb0c8' }} />
              </button>
            );
          })}
        </div>
      </div>

      <section className="panel" style={{ flexShrink: 0 }}>
        <div className="panel-head">
          <span className="lbl" style={{ flexGrow: 1 }}>
            Recent (expired)
          </span>
          <span className="mono muted" style={{ fontSize: 12.5 }}>
            {history ? `${expired.length} shown` : ''}
          </span>
        </div>
        {historyError && <div className="err-box" style={{ margin: 12 }}>Could not load history: {historyError}</div>}
        {!history && !historyError && <div className="empty">Loading history…</div>}
        {history && expired.length === 0 && <div className="empty">No expired alerts in recent history.</div>}
        {expired.map((ta) => {
          const a = ta.alert;
          const k = kindStyle(a.event);
          return (
            <button
              key={`${a.nwsId}-h`}
              type="button"
              className="hov alert-hist-row"
              onClick={() => navigate(`/alerts/${encodeURIComponent(a.nwsId)}`)}
              style={{
                width: '100%',
                border: 'none',
                borderBottom: '1px solid #18253c',
                background: 'transparent',
                textAlign: 'left',
                fontSize: 13.5,
              }}
            >
              <Icon d={KIND_ICON[k.kind]} size={18} style={{ color: k.color, opacity: 0.8 }} />
              <span style={{ fontWeight: 600, color: k.color }}>{a.event}</span>
              <span className="muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {a.areaDesc}
              </span>
              <span className="mono muted" style={{ fontSize: 12.5 }}>
                {fmtDateTime(a.sent, tz)}
              </span>
              <span className="tag tag-neutral" style={{ justifySelf: 'start' }}>
                {ta.evaluation?.state ?? (a.active ? 'active' : 'expired')}
              </span>
            </button>
          );
        })}
      </section>
    </div>
  );
}
